import Vue from 'vue';
import Vuex, { Commit } from 'vuex';
import axios, { AxiosResponse } from 'axios';

import { State, Question, Player } from '@/types';

Vue.use(Vuex);

const baseURL = 'https://koyki444lj.execute-api.eu-west-1.amazonaws.com/questions';

const initialState: State = {
  loading: false,
  queryCount: 0,
  randomQuestions: [],
  selectedQuestions: [],
  players: [],
};

const store = {
  state: initialState,

  mutations: {
    FETCH_RANDOM_QUESTIONS(state: State, questions: Array<Question>) {
      state.randomQuestions = questions.map((q) => ({ ...q, showAnswer: false, selected: false }));
      state.loading = false;
      state.queryCount += 1;
    },
    SHOW_ANSWER(state: State, id: number) {
      const foundIndex = state.randomQuestions.findIndex((q) => q.id === id);
      state.randomQuestions[foundIndex].showAnswer = true;
    },
    SHOW_ALL_ANSWERS(state: State) {
      state.randomQuestions = state.randomQuestions.map((q) => ({ ...q, showAnswer: true }));
    },
    SELECT_QUESTION(state: State, id: number) {
      if (state.selectedQuestions.length >= 100) return; // 100 selected is max

      const foundIndex = state.randomQuestions.findIndex((q) => q.id === id);
      const question = state.randomQuestions[foundIndex];

      if (state.selectedQuestions.findIndex((q) => q.id === question.id) === -1) {
        state.selectedQuestions.push(question);
        state.randomQuestions[foundIndex].selected = true;
      }
    },
    SELECT_ALL_QUESTIONS(state: State) {
      if (state.selectedQuestions.length >= 100) return; // 100 selected is max

      for (let i = 0; i < state.randomQuestions.length; i += 1) {
        const question = state.randomQuestions[i];
        if (question.selected === false
          && state.selectedQuestions.findIndex((q) => q.id === question.id) === -1) {
          state.selectedQuestions.push(question);
          question.selected = true;
        }
      }
    },

    DESELECT_QUESTION(state: State, id: number) {
      let foundIndex = state.selectedQuestions.findIndex((q) => q.id === id);
      const question = state.selectedQuestions[foundIndex];

      state.selectedQuestions.splice(foundIndex, 1);

      // Deselected in the random list
      foundIndex = state.randomQuestions.findIndex((q) => q.id === question.id);
      if (foundIndex > -1) state.randomQuestions[foundIndex].selected = false;
    },

    START_GAME(state: State, players: Array<Player>) {
      state.players = players;
    },

    UPDATE_SCORE(state: State, playersCorrect: Array<string>) {
      for (let i = 0; i < playersCorrect.length; i += 1) {
        const playerIdx = state.players.findIndex((p) => p.name === playersCorrect[i]);
        if (playerIdx > -1) state.players[playerIdx].score += 1;
      }

      state.players.sort((a: Player, b: Player) => ((a.score > b.score) ? -1 : 1));
    },

    RESET_SCORE(state: State) {
      state.players = state.players.map((p) => ({ ...p, score: 0 }));
    },

    RESET_SELECTED_QUESTIONS(state: State) {
      state.randomQuestions = state.randomQuestions.map((q) => ({ ...q, selected: false }));
      state.selectedQuestions = [];
    },

    LOADING(state: State, loading: boolean) {
      state.loading = loading;
    },
  },

  actions: {
    fetchRandomQuestions({ commit }: { commit: Commit }, params: object) {
      commit('LOADING', true);
      axios.get(baseURL, { params })
        .then((response: AxiosResponse) => {
          commit('FETCH_RANDOM_QUESTIONS', response.data);
        });
    },
    selectQuestion({ commit }: { commit: Commit }, id: number) {
      commit('SELECT_QUESTION', id);
    },
    deselectQuestion({ commit }: { commit: Commit }, id: number) {
      commit('DESELECT_QUESTION', id);
    },
    showAnswer({ commit }: { commit: Commit }, id: number) {
      commit('SHOW_ANSWER', id);
    },
    selectAllQuestions({ commit }: { commit: Commit }) {
      commit('SELECT_ALL_QUESTIONS');
    },
    showAllAnswers({ commit }: { commit: Commit }) {
      commit('SHOW_ALL_ANSWERS');
    },
    resetSelectedQuestions({ commit }: { commit: Commit }) {
      commit('RESET_SELECTED_QUESTIONS');
    },
    startGame({ commit }: { commit: Commit }, players: Array<Player>) {
      commit('START_GAME', players);
    },
    resetScore({ commit }: { commit: Commit }) {
      commit('RESET_SCORE');
    },
    updateScore({ commit }: { commit: Commit }, playersCorrect: Array<string>) {
      commit('UPDATE_SCORE', playersCorrect);
    },

  },

  getters: {
    randomQuestions(state: State) {
      return state.randomQuestions;
    },
    selectedQuestions(state: State) {
      return state.selectedQuestions;
    },
    players(state: State) {
      return state.players;
    },
    loading(state: State): boolean {
      return state.loading;
    },
    queryCount(state: State): number {
      return state.queryCount;
    },
  },
};

export default new Vuex.Store(store);

// https://github.com/DevoidCoding/TodoMVC-Vue-TypeScript/tree/master/vuex-typescript/src/store
// https://egghead.io/lessons/vue-js-create-a-vuex-store-using-typescript
